import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import dt from '../../../shared/theme';
import { darken } from '../../../shared/color';
import relativeTime from '../../../shared/relative-time';
import { propTypes, defaultProps } from './prop-types';
import Avatar from './avatar';
import CommentBody from './comment-body';
import Like from '../../shared/like';
import ContributorLike from './contributor-like';
import { like, unlike } from '../../../services/comment/like-service';
import LikeHint from './like-hint';

const Article = styled.article`
  background-color: transparent;

  > div {
    padding: 0 10px;

    > header {
      color: ${props => darken(dt(props).theme.secondaryColor, 10)};
      font-size: 0.85rem;
      text-transform: uppercase;
      display: flex;
      align-items: center;

      > p {
        flex-grow: 2;
        font-weight: ${props => dt(props).theme.fontWeightDccc};
        font-size: 14px;
        line-height: 120%;
        margin: 0 0 0 15px;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        > span {
          width: 100%;
          color: #627484;
          font-size: 16px;

          > a {
            color: #627484;
            transition: none;
            :hover {
              text-decoration: none;
            }
          }
        }

        > time {
          flex-grow: 2;
          font-weight: ${props => dt(props).theme.fontWeightHeavy};
          color: ${props => dt(props).theme.secondaryColor};
        }
      }
    }

    &.comment-main {
      margin: 35px 3px 10px 3px;
      border-left: 1px solid #DFE5EC;
    }

    &.comment-footer {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 0;

      div.contributor-like {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding-left: 12px;

        img.contributor-image {
          display: flex;
          justify-content: flex-start;
          align-items: center;  
          height: 24px;
          width: 24px;
          border: 1px solid #DFE6EC;
          filter: drop-shadow(0 2px 2px rgba(50,50,71,0.12));
          border-radius: 4px;
        }

        span.icon-lw-contributor-liked {
          position: relative;
          left: -14px;
          top: 5px;

          span {
            font-size: 26px;
            text-shadow: 0 1.09076px 2.18152px rgba(50, 50, 71, 0.2), 0 2.18152px 4.36304px rgba(50, 50, 71, 0.15);
          }
        } 
      }
    }  

    > .avatar {
      flex-shrink: 0;
      :hover {
        text-decoration: none;
      }
    }
  }
`;

const LikeWrapper = styled.span`
  @keyframes slide {
    from {
      transform: translateY(0);
    }
    to {
      transform: translateY(-19px);
    }
  }

  div.like-container {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-start;
    align-items: center;
    width: fit-content;
    height: 32px;
    border: 1px solid white;
    border-radius: 5px;
    margin-left: 8px;
    padding: 0 12px 0 4px;

    ${props => dt(props).theme.isMobile && `
      height: 42px;
      justify-content: center;
      align-items: center;
    `}

    &:focus {
      outline: 1px solid #627484;
      
      &.no-focus-outline {
        outline: none;
      }
    }

    > div.count-container {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: stretch;
      overflow-y: hidden;
      height: 24px;

      ${props => dt(props).theme.isMobile && `
        justify-content: center;
      `}

      span.count {
        font-weight: 600;
        color: #788B9B;
        width: fit-content;
        height: 20px;
        padding: 4px 0 0 0;

        ${props => dt(props).theme.isMobile && `
          padding: 2px 0 0 0;
        `}

        &:nth-of-type(2) {
          padding-top: 12px;
        }

        &.initial-value {
          padding: 4px 0 0 0;

          ${props => dt(props).theme.isMobile && `
            padding: 12px 0 0 0;
          `}
        }
      }

      &.count-animation {
        > span.count {
          animation: slide 0.2s cubic-bezier(0.5, 1, 0.89, 1);
        }
      }
    }

    div.icon-container {
      width: 32px;
      height: 32px;
      display: flex;
      justify-content: center;
      align-items: center;

      ${props => dt(props).theme.isMobile && `
        height: 41px;
        width: 37px;
        justify-content: center;
        align-items: center;
      `}

      > span.icon-lw {
        font-size: 24px;
        color: #627484;
        font-weight: 500;
        height: 24px;
        display: flex;
        justify-content: center;
        align-items: center;

        ${props => dt(props).theme.isMobile && `
          font-size: 28px;
          height: 28xp;
        `}
      }
    }

    &:hover {
      cursor: pointer;

      ${props => dt(props).theme.isLarge && `
        border: 1px solid #D7DFE7;
      `}

      > div.count-container span.count {
        color: black;
      }

      > div.icon-container span.icon-lw.icon-lw-like {
        color: black;
      }
    }
  }
`;

export default function Comment({
  id,
  body,
  profileName,
  profilePictureUrl,
  profileUrl,
  publishedAt,
  removable,
  wireSlug,
  likeCount,
  likedByUser,
  likedByContributor,
  contributorName,
  contributorProfilePicture,
  isFirstComment,
  showCommentLikeHint,
}) {
  const comment = {
    id, type: 'comment', likeCount, likedByUser, likedByContributor, contributorName, contributorProfilePicture,
  };

  const likeRef = React.useRef();

  return (
    <Article className="comment" id={`comment_${id}`}>
      <div className="comment-main">
        <header>
          <Avatar {...{ profilePictureUrl, profileUrl, name: profileName }} />
          <p>
            <span>
              {
                profileUrl
                  ? <a href={profileUrl}>{profileName}</a>
                  : profileName
              }
            </span>
            <time>{relativeTime(publishedAt, false)}</time>
            {!!removable && (
              <a
                className="remove-comment-link wire-remote"
                data-confirm="Are you sure you want to delete this comment?"
                data-remote="true"
                rel="nofollow"
                data-method="delete"
                href={`/wires/${wireSlug}/comments/${id}`}
              >
                remove comment
              </a>
            )}
          </p>
        </header>
        <CommentBody text={body} />
      </div>
      <div className="comment-footer">
        <LikeWrapper ref={likeRef}>
          <Like likeable={comment} like={like} unlike={unlike} returnToSection={`comment_${id}`} />
        </LikeWrapper>
        {showCommentLikeHint && isFirstComment && <LikeHint ref={likeRef} />}
        {!!likedByContributor && !!contributorProfilePicture && (
        <ContributorLike
          contributorName={contributorName}
          contributorProfilePicture={contributorProfilePicture}
        />
        )}
      </div>
    </Article>
  );
}

Comment.propTypes = {
  ...propTypes,
  wireSlug: PropTypes.string.isRequired,
  isFirstComment: PropTypes.bool.isRequired,
  showCommentLikeHint: PropTypes.bool.isRequired,
};
Comment.defaultProps = defaultProps;
